import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Star } from 'lucide-react'
import { calcNumberCompat, getCompatEntry } from '@yadviga/shared'
import { useSession } from '../../store/user.store'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { DateField } from '../../components/ui/DatePicker'

export function NumbersCompatRoute() {
  const { t } = useTranslation()
  const user = useSession((s) => s.user)
  const lang = (user?.language ?? 'ru') as 'ru' | 'en'
  const [partnerDate, setPartnerDate] = useState('')

  if (!user?.birthDate) return <NoBirthDate />

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(partnerDate)
  const result = valid ? calcNumberCompat(user.birthDate, partnerDate) : null
  const entry = result ? getCompatEntry(result.textKey, lang) : null

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader
        title={t('numbers.compat_title')}
        subtitle={t('numbers.compat_intro')}
        backTo="/numbers"
      />

      <div className="flex flex-col gap-3 rounded-3xl bg-white/70 p-5 ring-1 ring-sage-200/60">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="text-sage-700">{t('numbers.compat_partner_date')}</span>
          <DateField
            value={partnerDate}
            onChange={setPartnerDate}
            placeholder={t('numbers.compat_partner_date')}
            minAge={0}
            maxAge={100}
          />
        </label>
      </div>

      {result && entry && (
        <div className="flex flex-col gap-4 rounded-3xl bg-white/60 p-5 ring-1 ring-sage-200/60">
          <div className="flex items-center justify-around gap-3">
            <NumberCircle label={t('numbers.you')} num={result.numberA} />
            <span className="font-serif text-2xl text-sage-400">+</span>
            <NumberCircle label={t('numbers.partner')} num={result.numberB} />
          </div>
          <Stars value={entry.stars} />
          <p className="leading-relaxed text-sage-800">{entry.text}</p>
        </div>
      )}
    </div>
  )
}

function NumberCircle({ label, num }: { label: string; num: number }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-copper/15 font-serif text-3xl text-copper">
        {num}
      </div>
      <span className="text-[10px] uppercase tracking-wider text-sage-500">{label}</span>
    </div>
  )
}

function Stars({ value }: { value: 1 | 2 | 3 | 4 | 5 }) {
  return (
    <div className="flex items-center justify-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={i <= value ? 'h-5 w-5 fill-copper text-copper' : 'h-5 w-5 text-sage-300'}
          strokeWidth={1.6}
          aria-hidden
        />
      ))}
    </div>
  )
}

function NoBirthDate() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center gap-4 py-10 text-center text-sage-700">
      <PageHeader title={t('numbers.compat_title')} backTo="/numbers" />
      <p>{t('numbers.no_birth_date')}</p>
      <Button onClick={() => (window.location.href = '/profile')}>
        {t('numbers.add_birth_date')}
      </Button>
    </div>
  )
}
